import { Component, OnInit } from '@angular/core';
import { Product } from 'src/app/model/product.model';
import { ProductService } from 'src/app/service/product.service';
import { ColDef, GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { compareVersions, dataFileNameFormatter, getReadableSubtype, numberFormatter, alphaNumericComparator } from '../utility-functions';

declare function showProductDetailModal(productToken: string): void;

@Component({
  selector: 'app-shading-list',
  templateUrl: './shading-list.component.html',
  styleUrls: ['./shading-list.component.scss'],
  standalone: false
})
export class ShadingListComponent implements OnInit {

  /* PUBLIC PROPERTIES */

  public autoGroupColumnDef = {
    minWidth: 250,
    comparator: alphaNumericComparator,
  };
  public context = {
    this: this
  };
  public rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' | undefined = 'always';
  public suppressDragLeaveHidesColumns = true;
  public rowSelection: 'single' | 'multiple' | undefined = 'single';
  public rowDeselection = true;
  public downloadingProduct = false;
  public downloadingProductDataFile = false;
  public statusBar = {
    statusPanels: [
      {
        statusPanel: 'agTotalRowCountComponent',
        align: 'left'
      },
      {
        statusPanel: 'agFilteredRowCountComponent'
      },
      {
        statusPanel: 'agSelectedRowCountComponent'
      }
    ]
  }
  public gridOptions = {};

  public defaultColDef = {
    width: 100,
    sortable: true,
    resizable: true,
    filter: true
  };

  public columnDefs: ColDef[] = [
    {
      headerName: 'ID',
      hide: true,
      field: 'id',
      filter: 'agTextColumnFilter',
      width: 200
    },
    {
      headerName: 'Subtype',
      field: 'subtype',
      enableRowGroup: true,
      valueGetter: (params: any) => getReadableSubtype(params.data),
      headerClass: 'groupable-column',
      width: 220,
    },
    {
      headerName: 'Manufacturer',
      field: 'manufacturer_name',
      enableRowGroup: true,
      rowGroup: true,
      headerClass: 'groupable-column',
      filter: 'agTextColumnFilter',
      comparator: alphaNumericComparator,
      // Default ascending sort so grid loads already sorted by Manufacturer
      width: 200,
      sort: 'asc',
    },
    {
      headerName: 'Name',
      field: 'name',
      filter: 'agTextColumnFilter',
      comparator: alphaNumericComparator,
      width: 150,
    },
    /* with SHADING product, product name has more relevance as it can be different than name */
    {
      headerName: 'Product Name',
      field: 'marketing_name',
      filter: 'agTextColumnFilter',
      comparator: alphaNumericComparator,
      width: 150
    },
    {
      headerName: 'Filename',
      field: 'data_file_name',
      filter: 'agTextColumnFilter',
      valueGetter: dataFileNameFormatter,
      comparator: alphaNumericComparator,
      width: 200
    },
    {
      headerName: 'CGDB ID',
      field: 'cgdb_id',
      filter: 'agNumberColumnFilter',
      width: 180
    },
    {
      headerName: 'Acceptance',
      field: 'acceptance',
      headerTooltip: '# = NFRC, @ = AERC',
      enableRowGroup: true,
      headerClass: 'groupable-column',
      filter: 'agTextColumnFilter',
      width: 150,
    },
    {
      headerName: 'TIR f',
      field: 'tir_front',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the infra-red spectrum, of the front of the layer',
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    // {headerName: 'TIR b', field: 'tir_back', valueFormatter: numberFormatter },
    {
      headerName: 'Emis f',
      field: 'emissivity_front',
      headerTooltip: 'Emittance of the front surface of the layer',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 120
    },
    {
      headerName: 'Emis b',
      field: 'emissivity_back',
      valueFormatter: numberFormatter,
      headerTooltip: 'Emittance of the back surface of the layer',
      filter: 'agNumberColumnFilter',
      width: 120
    },
    {
      headerName: 'tf sol\n(dir-dir)',
      field: 'tf_sol',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the solar spectrum, of the front of the layer (direct-direct)',
      filter: 'agNumberColumnFilter',
      width: 200,
    },
    {
      headerName: 'tf sol (dir-dif)',
      field: 'tf_sol_dir_dif',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the solar spectrum, of the front of the layer (direct-diffuse)',
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'tf sol (dir-hem)',
      field: 'tf_sol_dir_hem',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the solar spectrum, of the front of the layer (direct-hemispherical)',
      filter: 'agNumberColumnFilter'
    },

    {
      headerName: 'rf sol (dir-dir)',
      field: 'rf_sol',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the solar spectrum, of the front of the layer (direct-direct)',
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'rf sol (dir-dif)',
      field: 'rf_sol_dir_dif',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the solar spectrum, of the front of the layer (direct-diffuse)',
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'rf sol (dir-hem)',
      field: 'rf_sol_dir_hem',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the solar spectrum, of the front of the layer (direct-hemispherical)',
      filter: 'agNumberColumnFilter'
    },

    {
      headerName: 'rb sol (dir-dir)',
      field: 'rb_sol',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the solar spectrum, of the back of the layer (direct-direct)',
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'rb sol (dir-dif)',
      field: 'rb_sol_dir_dif',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the solar spectrum, of the back of the layer (direct-diffuse)',
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'rb sol (dir-hem)',
      field: 'rb_sol_dir_hem',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the solar spectrum, of the back of the layer (direct-hemispherical)',
      filter: 'agNumberColumnFilter'
    },

    {
      headerName: 'tf vis (dir-dir)',
      field: 'tf_vis',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the visible spectrum, of the front of the layer (direct-direct)',
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'tf vis (dir-dif)',
      field: 'tf_vis_dir_dif',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the visible spectrum, of the front of the layer (direct-diffuse)',
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'tf vis (dir-hem)',
      field: 'tf_vis_dir_hem',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the visible spectrum, of the front of the layer (direct-hemispherical)',
      filter: 'agNumberColumnFilter'
    },

    {
      headerName: 'rf vis (dir-dir)',
      field: 'rf_vis',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the visible spectrum, of the front of the layer (direct-direct)',
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'rf vis (dir-dif)',
      field: 'rf_vis_dir_dif',
      width: 200,
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the visible spectrum, of the front of the layer (direct-diffuse)',
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'rf vis (dir-hem)',
      field: 'rf_vis_dir_hem',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the visible spectrum, of the front of the layer (direct-hemispherical)',
      filter: 'agNumberColumnFilter',
      width: 200,
    },

    {
      headerName: 'rb vis (dir-dir)',
      field: 'rb_vis',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the visible spectrum, of the back of the layer (direct-direct)',
      filter: 'agNumberColumnFilter',
      width: 200,
    },
    {
      headerName: 'rb vis (dir-dif)',
      field: 'rb_vis',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the visible spectrum, of the back of the layer (direct-diffuse)',
      filter: 'agNumberColumnFilter',
      width: 200,
    },
    {
      headerName: 'rb vis (dir-hem)',
      field: 'rb_vis_dir_hem',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the visible spectrum, of the back of the layer (direct-hemispherical)',
      filter: 'agNumberColumnFilter',
      width: 200,
    },
    {
      headerName: 'rb vis (dir-hem)',
      field: 'rb_vis_dir_hem',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the visible spectrum, of the back of the layer (direct-hemispherical)',
      filter: 'agNumberColumnFilter',
      width: 205
    },
    {
      headerName: 'Conductivity',
      field: 'conductivity',
      width: 150,
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: '# Product Detail Files',
      field: 'num_product_detail_files',
      valueFormatter: numberFormatter,
      headerTooltip: 'Product details files attached to this product',
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'Perm',
      field: 'permeability_factor',
      valueFormatter: numberFormatter,
      headerTooltip: 'Permeability',
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'CGDB Version',
      field: 'cgdb_database_version',
      headerTooltip: '',
      enableRowGroup: true,
      headerClass: 'groupable-column',
      filter: 'agNumberColumnFilter',
      comparator: compareVersions,
      width: 150,
    },
    {
      headerName: 'Specular',
      field: 'is_specular',
      cellRenderer: (params: any) => {
        if (params.value === null || params.value === undefined) {
          return '';
        }
        return params.value ? 'Yes' : 'No';
      },
      enableRowGroup: true,
      headerClass: 'groupable-column',
      width: 150,
    },
    {
      headerName: 'Thickness (mm)',
      field: 'thickness',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'Token',
      field: 'token',
      filter: 'agTextColumnFilter',
      width: 150
    },

  ];

  public sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressPivots: true,
          suppressPivotMode: true,
        }
      },
      {
        id: 'filters',
        labelKey: 'filters',
        labelDefault: 'Filters',
        iconKey: 'menu',
        toolPanel: 'agFiltersToolPanel',
      }
    ],
    defaultToolPanel: '', // Collapses toolpanel
  };


  // Import to set this to null initially so
  // Ag-Grid shows 'loading' message.
  public shadingList: Product[] | null = null;

  /* PRIVATE PROPERTIES */

  private gridApi: any;

  /* METHODS */

  constructor(private productService: ProductService) {
  }

  ngOnInit() {
    this.productService
      .getProductList(this.productService.TYPE_SHADING)
      .subscribe({
        next: data => {
          this.shadingList = data;
        },
        error: error => {
          console.error('Error trying to download shading records: ', error);
        },
        complete: () => {
          console.log(`Completed server request for shading records. Loaded ${this.shadingList?.length} records.`);
        }
      });
  }

  gridReady(params: any) {
    this.gridApi = params.api;
  }

  onSelectionChanged(event: any) {
    const selectedRows = this.gridApi.getSelectedRows();
    if (selectedRows.length === 1) {
      const selectedItem: Product = selectedRows[0];
      this.productService.selectedItem = selectedItem;
    } else {
      // Row was deselected (or more than one row, which shouldn't happen
      // since we should be on single select seting)
      this.productService.selectedItem = null;
    }
  }

  onRowDoubleClicked(event: any) {
    if (event.data) {
      const token = event.data.token;
      if (event.shiftKey) {
        const url = `/products/${token}`;
        window.open(url, '_blank');
      } else {
        showProductDetailModal(token);
      }
    }
  }

  downloadProduct(product: Product, format: string) {
    this.downloadingProduct = true;
    this.productService.openNewTabWithProduct(product, format);
  }

  downloadProductDataFile(product: Product) {
    this.downloadingProductDataFile = true;
    this.productService.openNewTabWithProductDataFile(product)
  }

  shadingListContextMenu(params: GetContextMenuItemsParams): (string | MenuItemDef)[] {

    if (params.node === null || params.node.data === null) {
      return [];
    }

    const product: Product = params.node.data;

    // Always show CSV and JSON download options
    const result: Array<MenuItemDef> = [
      {
        name: 'Open in new tab',
        action: () => {
          const url = `/products/${product.token}`;
          window.open(url, '_blank');
        },
      },
      {
        name: 'Download product CSV',
        action: () => {
          params.context.this.downloadProduct(product, 'csv');
        }
      },
      {
        name: 'View product JSON',
        action: () => {
          params.context.this.downloadProduct(product, 'json');
        }
      }
    ];

    // Decide whether to show data file download
    /*
    if (product.data_file_name) {
      // There's a filename, so we have to show a live or disabled download.
      // Either way, we *always* show the context menu item so the user doesn't get confused
      // if it doesn't appear.

      let shortDataFileName = product.data_file_name;
      if (shortDataFileName.length > 30) {
        shortDataFileName = `${shortDataFileName.substring(0, 30)}...`;
      }

      if (product.data_file_available) {
        result.push({
          name: `Download product datafile '${shortDataFileName}'`,
          action: () => {
            params.context.this.downloadProductDataFile(product);
          }
        });
      } else {
        result.push(
          {
            disabled: true,
            name: `Product datafile '${shortDataFileName}' unavailable for download`
          }
        );
      }
    }
    */
    return result;
  }

  onDeselectAll() {
    this.gridApi.deselectAll();
  }

  // Provide initial group order to guarantee deterministic ordering on first render
  initialGroupOrderComparator(params: any) {
    const a = (params.nodeA.key || '').toString();
    const b = (params.nodeB.key || '').toString();
    return alphaNumericComparator(a, b);
  }


}


